import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faProjectDiagram,
  faSpinner,
  faCheckCircle,
  faFolder,
  faClock,
  faPlus,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import projectService from '../services/projectService';
import actionService from '../services/actionService';
import { format } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/common/PageHeader';

const Dashboard = () => {
  const { t } = useLanguage();
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentActions, setRecentActions] = useState([]);
  const [projectStats, setProjectStats] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch projects
        const projectsResponse = await projectService.getProjects();
        const projects = Array.isArray(projectsResponse) ? projectsResponse : [];
        setRecentProjects(projects.slice(0, 5));

        // Calculate project statistics
        const total = projects.length;
        const inProgress = projects.filter(p => p.status === 'in-progress').length;
        const completed = projects.filter(p => p.status === 'completed').length;
        setProjectStats({ total, inProgress, completed });

        // Fetch recent actions
        const actionsResponse = await actionService.getActions();
        const actions = Array.isArray(actionsResponse) ? actionsResponse : [];
        setRecentActions(actions.slice(0, 10));

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-10">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-primary mb-4" />
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t('dashboard.title')} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-primary" />
            {t('dashboard.projectOverview')}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{t('executive.totalProjects')}</span>
              <span className="font-bold text-lg text-gray-900 dark:text-white">{projectStats.total}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{t('actions.inProgress')}</span>
              <span className="font-bold text-lg text-blue-600 dark:text-blue-400">{projectStats.inProgress}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{t('projects.completed')}</span>
              <span className="font-bold text-lg text-green-600 dark:text-green-400">{projectStats.completed}</span>
            </div>
          </div>
          <div className="mt-4">
            <Link
              to="/projects"
              className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
            >
              {t('projectDetail.viewAll')} {t('nav.projects').toLowerCase()}
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 md:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold flex items-center text-gray-900 dark:text-white">
              <FontAwesomeIcon icon={faFolder} className="mr-2 text-primary" />
              {t('projectDetail.recentActions')}
            </h3>
            <Link
              to="/projects/new"
              className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 text-sm rounded-md flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              {t('projects.newProject')}
            </Link>
          </div>
          {recentProjects.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">{t('projects.noProjects')}</p>
          ) : (
            <div className="space-y-2">
              {recentProjects.map((project) => (
                <Link
                  key={project._id}
                  to={`/projects/${project._id}`}
                  className="block p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{project.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{project.client}</p>
                    </div>
                    <div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.status === 'planning'
                            ? 'bg-yellow-100 text-yellow-800'
                            : project.status === 'in-progress'
                            ? 'bg-blue-100 text-blue-800'
                            : project.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {t(`projects.${project.status}`) || project.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold flex items-center text-gray-900 dark:text-white">
            <FontAwesomeIcon icon={faClock} className="mr-2 text-primary" />
            {t('projectDetail.recentActions')}
          </h3>
        </div>
        {recentActions.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">{t('actions.noActions')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('layout.project')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    MITRE ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('actions.result')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {recentActions.map((action) => (
                  <tr key={action._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {format(new Date(action.startTimestamp || action.timestamp), 'MMM d, yyyy HH:mm')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      <Link
                        to={`/projects/${action.project._id || action.project}/actions/${action._id}`}
                        className="hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        {action.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {typeof action.project === 'object' ? action.project.name : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {action.mitreAttackTechnique ? action.mitreAttackTechnique.id : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          action.result === 'successful'
                            ? 'bg-green-100 text-green-800'
                            : action.result === 'detected'
                            ? 'bg-yellow-100 text-yellow-800'
                            : action.result === 'blocked'
                            ? 'bg-red-100 text-red-800'
                            : action.result === 'failed'
                            ? 'bg-gray-100 text-gray-800'
                            : action.result === 'partial'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {t(`actions.${action.result}`) || action.result}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
